import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Check, ChevronLeft, ChevronRight, ListChecks, Loader2, Sparkles, AlertTriangle, CloudUpload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { STEPS, blankWizard, normalizeWizard, toConsultationFields, missingChecks } from "@/components/wizard/config";
import { ConfirmDialog } from "@/components/wizard/Controls";
import StepPatient from "@/components/wizard/StepPatient";
import StepExamination from "@/components/wizard/StepExamination";
import StepHistory from "@/components/wizard/StepHistory";
import StepUpload from "@/components/wizard/StepUpload";
import StepReview from "@/components/wizard/StepReview";

function StepNav({ step, reached, goTo, done }) {
  return (
    <nav aria-label="Consultation steps" className="space-y-1" data-testid="wizard-step-nav">
      {STEPS.map((s) => {
        const can = s.n <= reached;
        const current = s.n === step;
        return (
          <button key={s.n} type="button" disabled={!can} onClick={() => goTo(s.n)} aria-current={current ? "step" : undefined} data-testid={`wizard-nav-step-${s.n}`}
            className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A85A]",
              current ? "bg-[#E8F7F0] font-bold text-[#008F4D] dark:bg-[#00A85A]/15 dark:text-[#3FD98A]" : can ? "text-[#101516] hover:bg-[#F4FBF7] dark:text-white dark:hover:bg-white/5" : "cursor-not-allowed text-[#B7C3C7] dark:text-[#3A4A52]")}>
            <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold", done(s.n) ? "bg-[#00A85A] text-white" : current ? "border-2 border-[#00A85A] text-[#00A85A]" : "border border-current")}>
              {done(s.n) ? <Check className="h-3.5 w-3.5" /> : s.n}
            </span>
            {s.label}
          </button>
        );
      })}
    </nav>
  );
}

export default function ConsultationWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [w, setW] = useState(null);
  const [docs, setDocs] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const slug = c?.condition || "implant";
  const conditionName = conditions.find((x) => x.slug === slug)?.name || c?.condition_name || "Dental Implant";

  const loadDocs = useCallback(() => api.get(`/imaging?consultation_id=${id}`).then((r) => setDocs(r.data)).catch(() => {}), [id]);
  useEffect(() => {
    api.get(`/consultations/${id}`).then((r) => { setC(r.data); setW(normalizeWizard(r.data.wizard, r.data)); }).catch(() => { toast.error("Consultation not found"); navigate("/consultation/new"); });
    api.get("/conditions").then((r) => setConditions(r.data)).catch(() => {});
    loadDocs();
  }, [id, navigate, loadDocs]);

  const analysing = docs.some((d) => (d.content_type || "").startsWith("image/") && !d.ai_assessment && d.assessment_status === "analyzing");
  useEffect(() => {
    if (!analysing) return undefined;
    const iv = setInterval(loadDocs, 4000);
    return () => clearInterval(iv);
  }, [analysing, loadDocs]);

  const save = useCallback((next, extra = {}) => {
    clearTimeout(timer.current);
    setSaving(true);
    timer.current = setTimeout(() => {
      api.put(`/consultations/${id}`, { wizard: next, ...toConsultationFields(next, extra.condition || slug), ...extra })
        .catch(() => toast.error("Couldn't save — check your connection"))
        .finally(() => setSaving(false));
    }, 600);
  }, [id, slug]);

  const update = (fn) => setW((prev) => { const n = fn(prev); const out = prev.approved_at ? { ...n, stale: true } : n; save(out); return out; });
  const goTo = (n) => {
    setW((prev) => { const out = { ...prev, step: n, reached: Math.max(prev.reached, n) }; save(out); return out; });
    requestAnimationFrame(() => { document.querySelector("main")?.scrollTo({ top: 0 }); window.scrollTo({ top: 0 }); });
  };

  const checks = useMemo(() => (w ? missingChecks(w, slug, docs) : { red: [], amber: [] }), [w, slug, docs]);
  const pendingFindings = docs.reduce((n, d) => n + (d.review_items || []).filter((x) => x.status === "pending").length, 0);

  const next = async () => {
    if (w.step === 1) {
      const p = w.patient;
      if (!p.patient_id && !p.name.trim()) { toast.error(p.mode === "existing" ? "Select a patient or add a new one" : "Patient name is required"); return; }
      if (p.mode === "new") {
        const body = { name: p.name.trim(), age: p.age ? Number(p.age) : null, gender: p.gender, phone: p.phone, email: p.email };
        try {
          if (p.patient_id) await api.put(`/patients/${p.patient_id}`, body);
          else { const { data } = await api.post("/patients", body); update((d) => ({ ...d, patient: { ...d.patient, patient_id: data.id } })); toast.success("Patient created"); }
        } catch { toast.error("Couldn't save the patient"); return; }
      }
    }
    goTo(Math.min(5, w.step + 1));
  };

  const changeCondition = (nextSlug) => {
    const cd = conditions.find((x) => x.slug === nextSlug);
    setC((prev) => ({ ...prev, condition: nextSlug, condition_name: cd?.name }));
    setW((prev) => {
      const fresh = blankWizard(null).exam;
      const out = { ...prev, exam: { ...prev.exam, [slug]: fresh[slug] }, ...(prev.approved_at ? { stale: true } : {}) };
      save(out, { condition: nextSlug, condition_name: cd?.name });
      return out;
    });
    toast.success(`Condition changed to ${cd?.name}`);
  };

  const completeReview = () => {
    setConfirm(false);
    setW((prev) => { const out = { ...prev, approved_at: new Date().toISOString(), stale: false }; save(out); return out; });
    toast.success("Review completed");
  };
  const generate = async () => {
    setBusy(true);
    clearTimeout(timer.current);
    try {
      await api.put(`/consultations/${id}`, { wizard: w, ...toConsultationFields(w, slug), status: "reviewed" });
      const { data } = await api.post("/ai/treatment-plan", { consultation_id: id });
      toast.success("Report generation started — doctor approval required.");
      navigate(`/treatment-plan/${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (!/consultations in your plan/.test(d || "")) toast.error(typeof d === "string" ? d : "Couldn't start report generation");
      setBusy(false);
    }
  };

  if (!w || !c) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#00A85A]" /></div>;
  const done = (n) => n < w.reached || (n === 5 && w.approved_at && !w.stale);
  const approved = w.approved_at && !w.stale;
  const step = STEPS[w.step - 1];

  return (
    <div className="mx-auto max-w-6xl" data-testid="consultation-wizard">
      <div className="mb-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#00A85A]">{conditionName} consultation{c.mode === "voice" ? " · from voice" : ""}</div>
            <h1 className="truncate text-xl font-extrabold tracking-tight text-[#101516] dark:text-white sm:text-2xl">{step.label}</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-1 text-[11px] text-[#8B9698] sm:inline-flex" data-testid="wizard-save-state">{saving ? <><Loader2 className="h-3 w-3 animate-spin" />Saving</> : <><CloudUpload className="h-3 w-3" />Saved</>}</span>
            <Button variant="outline" size="sm" className="lg:hidden" onClick={() => setDrawer(true)} data-testid="wizard-steps-drawer-btn"><ListChecks className="mr-1.5 h-4 w-4" />Steps</Button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="shrink-0 text-xs font-semibold text-[#687173] dark:text-[#8B9698]" data-testid="wizard-step-indicator">Step {w.step} of 5</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E2E8F0] dark:bg-[#223139]" role="progressbar" aria-valuemin={1} aria-valuemax={5} aria-valuenow={w.step}>
            <div className="h-full rounded-full bg-[#00A85A] transition-[width] duration-500 motion-reduce:transition-none" style={{ width: `${(w.step / 5) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[230px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-4 rounded-2xl border border-[#E2E8F0] bg-white p-3 dark:border-[#223139] dark:bg-[#0E1A21]"><StepNav step={w.step} reached={w.reached} goTo={goTo} done={done} /></div></aside>
        <main className="min-w-0 pb-6">
          {w.step === 5 && w.approved_at && w.stale && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="needs-reapproval-banner"><AlertTriangle className="h-4 w-4" />Changes were made after approval — this review needs re-approval.</div>
          )}
          {w.step === 1 && <StepPatient w={w} update={update} />}
          {w.step === 2 && <StepExamination w={w} update={update} slug={slug} conditionName={conditionName} conditions={conditions} onChangeCondition={changeCondition} />}
          {w.step === 3 && <StepHistory w={w} update={update} slug={slug} />}
          {w.step === 4 && <StepUpload consultationId={id} slug={slug} docs={docs} setDocs={setDocs} reload={loadDocs} w={w} update={update} />}
          {w.step === 5 && <StepReview w={w} slug={slug} conditionName={conditionName} docs={docs} setDocs={setDocs} goTo={goTo} checks={checks} />}
        </main>
      </div>

      <div className="sticky bottom-0 z-30 -mx-4 border-t border-[#E2E8F0] bg-white/90 backdrop-blur dark:border-[#223139] dark:bg-[#07131A]/90 sm:mx-0 sm:rounded-t-2xl">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <Button variant="outline" disabled={w.step === 1} onClick={() => goTo(w.step - 1)} data-testid="wizard-back-btn"><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>
          {w.step < 5 ? (
            <Button onClick={next} className="bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid="wizard-next-btn">Next<ChevronRight className="ml-1 h-4 w-4" /></Button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {pendingFindings > 0 && <span className="text-[11px] font-semibold text-amber-600" data-testid="wizard-pending-findings">{pendingFindings} AI finding(s) to review</span>}
              {!approved ? (
                <Button disabled={checks.red.length > 0} onClick={() => setConfirm(true)} className="bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid="complete-review-btn"><ShieldCheck className="mr-1.5 h-4 w-4" />{w.approved_at ? "Re-approve Review" : "Complete Review"}</Button>
              ) : (
                <Button disabled={busy || analysing || pendingFindings > 0} onClick={generate} className="bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid="generate-report-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Generate Treatment Plan & Report</Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="w-72" data-testid="wizard-steps-drawer">
          <SheetHeader><SheetTitle>Consultation steps</SheetTitle></SheetHeader>
          <div className="mt-4"><StepNav step={w.step} reached={w.reached} goTo={(n) => { goTo(n); setDrawer(false); }} done={done} /></div>
        </SheetContent>
      </Sheet>
      <ConfirmDialog open={confirm} title={w.approved_at ? "Re-approve this review?" : "Complete the review?"}
        description={checks.amber.length ? `${checks.amber.length} optional item(s) are still missing. You can still complete the review.` : "You confirm the consultation details are correct and ready for report generation."}
        confirmLabel={w.approved_at ? "Re-approve" : "Complete Review"} onConfirm={completeReview} onCancel={() => setConfirm(false)} />
    </div>
  );
}
